import { useEffect, useMemo, useRef } from 'react';

import {
  GridPaginationModel,
  GridRowSelectionModel,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';

import { sumGroupItemPrice } from '../GeneratikTable.utils';

export interface UseTableTotalsParams<R extends GridValidRowModel> {
  rows: R[];
  paginationMode: 'client' | 'server';
  paginationModel: GridPaginationModel;
  selectionModel: GridRowSelectionModel;
  resolveRowId: (row: R) => string | number;
  calculatePageTotal?: (rows: R[]) => number | string;
  calculateSelectedTotal?: (rows: R[]) => number | string;
  getGroupItemPrice?: (row: R) => number | undefined;
  persistSelectedRows?: boolean;
  onSelectedRowsChange?: (selectedRows: R[]) => void;
}

export interface UseTableTotalsReturn<R extends GridValidRowModel> {
  pageRows: R[];
  selectedRows: R[];
  pageTotal: number | string | undefined;
  pageSelected: number | string | undefined;
}

const useTableTotals = <R extends GridValidRowModel>({
  rows,
  paginationMode,
  paginationModel,
  selectionModel,
  resolveRowId,
  calculatePageTotal,
  calculateSelectedTotal,
  getGroupItemPrice,
  persistSelectedRows,
  onSelectedRowsChange,
}: UseTableTotalsParams<R>): UseTableTotalsReturn<R> => {
  const pageRows = useMemo(() => {
    if (paginationMode === 'server') return rows;
    const start = paginationModel.page * paginationModel.pageSize;
    return rows.slice(start, start + paginationModel.pageSize);
  }, [rows, paginationModel, paginationMode]);

  const selectedRows = useMemo(() => {
    if (selectionModel.length === 0) return [];
    const selectedIds = new Set(selectionModel.map(String));
    return rows.filter((row) => selectedIds.has(String(resolveRowId(row))));
  }, [rows, selectionModel, resolveRowId]);

  const pageTotal = useMemo(() => {
    if (calculatePageTotal) return calculatePageTotal(pageRows);
    if (getGroupItemPrice) return sumGroupItemPrice(pageRows, getGroupItemPrice);
    return undefined;
  }, [calculatePageTotal, getGroupItemPrice, pageRows]);

  const pageSelected = useMemo(() => {
    if (calculateSelectedTotal) return calculateSelectedTotal(selectedRows);
    if (getGroupItemPrice && selectionModel.length > 0) {
      const selectedIds = new Set(selectionModel.map(String));
      return sumGroupItemPrice(
        pageRows.filter((row) => selectedIds.has(String(resolveRowId(row)))),
        getGroupItemPrice,
      );
    }
    return undefined;
  }, [
    calculateSelectedTotal,
    getGroupItemPrice,
    pageRows,
    resolveRowId,
    selectionModel,
    selectedRows,
  ]);

  const hasRestoredSelection = useRef(false);
  useEffect(() => {
    if (hasRestoredSelection.current) return;
    if (!persistSelectedRows || selectionModel.length === 0 || rows.length === 0) return;

    hasRestoredSelection.current = true;
    const selectedIds = new Set(selectionModel.map(String));
    const restoredRows = rows.filter((row) => selectedIds.has(String(resolveRowId(row))));
    onSelectedRowsChange?.(restoredRows);
  }, [persistSelectedRows, selectionModel, rows, resolveRowId, onSelectedRowsChange]);

  return { pageRows, selectedRows, pageTotal, pageSelected };
};

export default useTableTotals;
